import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getPhaserGameSync, isPhaserGameReady } from '../PhaserGame'

interface WhiteboardState {
    whiteboardDialogOpen: boolean
    whiteboardId: null | string
    whiteboardUrl: null | string
    urls: Record<string, string>
    connectionStatus: Record<string, 'connecting' | 'connected' | 'failed' | 'timeout'>
    connectionTimeouts: Record<string, NodeJS.Timeout>
    /** whiteboardId → array of session IDs currently using that whiteboard */
    activeWhiteboardUsers: Record<string, string[]>
    /** whiteboardId → virtual room ID (null/undefined = public whiteboard) */
    whiteboardVrIds: Record<string, string>
    /** Server-bumped counter; included in WBO board URLs so a reset yields a blank canvas. */
    whiteboardEpoch: number
}

const initialState: WhiteboardState = {
    whiteboardDialogOpen: false,
    whiteboardId: null,
    whiteboardUrl: null,
    urls: {},
    connectionStatus: {},
    connectionTimeouts: {},
    activeWhiteboardUsers: {},
    whiteboardVrIds: {},
    whiteboardEpoch: 0,
}

// Generate a consistent color based on roomId and whiteboardId
const generateWhiteboardColor = (roomId: string, whiteboardId: string): string => {
    // Create a hash from the combined room and whiteboard IDs
    const combinedId = `${roomId}-${whiteboardId}`
    let hash = 0

    for (let i = 0; i < combinedId.length; i++) {
        hash = (hash << 5) - hash + combinedId.charCodeAt(i)
        hash |= 0 // Convert to 32bit integer
    }

    // Use predefined colors that work well for whiteboard backgrounds
    const colors = [
        'ffffff', // White (default)
        'f8f9fa', // Very light gray
        'f5f5ff', // Very light blue
        'f0f4f8', // Light blue gray
        'f5fcff', // Very light cyan
        'f9f9f9', // Off-white
        'fafafa', // Another off-white
        'fffaf0', // Floral white
        'fffff0', // Ivory
        'f0f8ff', // Alice blue
    ]

    // Get a positive index from the hash
    const colorIndex = Math.abs(hash) % colors.length
    return colors[colorIndex]
}

export const whiteboardSlice = createSlice({
    name: 'whiteboard',
    initialState,
    reducers: {
        openWhiteboardDialog: (state, action: PayloadAction<string>) => {
            // Safely access Phaser game (may not be initialized yet)
            const phaserGame = getPhaserGameSync()
            if (phaserGame && isPhaserGameReady()) {
                const game = phaserGame.scene?.keys?.game as any
                game?.disableKeys?.()
            }

            state.whiteboardDialogOpen = true
            state.whiteboardId = action.payload
            state.whiteboardUrl = state.urls[action.payload] || null
            state.connectionStatus[action.payload] = 'connecting'

            // Set up connection timeout (10 seconds)
            const timeoutId = setTimeout(() => {
                // We'll handle this timeout in the component instead
                // to avoid circular dependency issues
            }, 10000)
            state.connectionTimeouts[action.payload] = timeoutId
        },
        closeWhiteboardDialog: (state) => {
            // Safely access Phaser game (may not be initialized yet)
            const phaserGame = getPhaserGameSync()
            const game = phaserGame && isPhaserGameReady() ? phaserGame.scene?.keys?.game as any : null

            if (game) {
                game.enableKeys?.()
            }

            if (state.whiteboardId) {
                // Clear timeout if it exists
                const timeoutId = state.connectionTimeouts[state.whiteboardId]
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }

                // NOTE: disconnectFromWhiteboard is now handled by WhiteboardModal's
                // cleanup effect (only the initiator disconnects). Removed from here
                // to prevent old-flow side effects from interfering with the new modal flow.

                delete state.connectionStatus[state.whiteboardId]
                delete state.connectionTimeouts[state.whiteboardId]
            }

            state.whiteboardDialogOpen = false
            state.whiteboardId = null
            state.whiteboardUrl = null
        },
        setWhiteboardUrls: (state, action: PayloadAction<{ whiteboardId: string; roomId: string }>) => {
            // Use a reliable WBO server (first try WBO's official server)
            const wboServer = 'https://wbo.sevv.com'

            // Sanitize roomId and whiteboardId to remove dots and other problematic characters
            const sanitizedRoomId = action.payload.roomId.replace(/[.]/g, '_')
            const sanitizedWhiteboardId = action.payload.whiteboardId.replace(/[.]/g, '_')

            // Include epoch so a server reset yields a fresh WBO board
            const epochSuffix = state.whiteboardEpoch > 0 ? `-e${state.whiteboardEpoch}` : ''
            const boardId = `sevv-${sanitizedRoomId}-wb${sanitizedWhiteboardId}${epochSuffix}`

            // Generate a background color for this whiteboard
            const bgColor = generateWhiteboardColor(action.payload.roomId, action.payload.whiteboardId)

            // Create the URL with background color parameter
            const whiteboardUrl = `${wboServer}/boards/${boardId}?background=${bgColor}&toolbar=1`

            state.urls[action.payload.whiteboardId] = whiteboardUrl
            if (!state.connectionStatus[action.payload.whiteboardId]) {
                state.connectionStatus[action.payload.whiteboardId] = 'connecting'
            }
        },
        setWhiteboardEpoch: (state, action: PayloadAction<number>) => {
            state.whiteboardEpoch = action.payload
            // Clear all cached URLs so they get regenerated with the new epoch
            state.urls = {}
        },
        addWhiteboardUser: (state, action: PayloadAction<{ whiteboardId: string; sessionId: string }>) => {
            const { whiteboardId, sessionId } = action.payload
            const current = state.activeWhiteboardUsers[whiteboardId] || []
            if (!current.includes(sessionId)) {
                state.activeWhiteboardUsers[whiteboardId] = [...current, sessionId]
            }
        },
        removeWhiteboardUser: (state, action: PayloadAction<{ whiteboardId: string; sessionId: string }>) => {
            const { whiteboardId, sessionId } = action.payload
            const current = state.activeWhiteboardUsers[whiteboardId] || []
            const filtered = current.filter((id) => id !== sessionId)
            if (filtered.length > 0) {
                state.activeWhiteboardUsers[whiteboardId] = filtered
            } else {
                delete state.activeWhiteboardUsers[whiteboardId]
            }
        },
        setWhiteboardVrId: (state, action: PayloadAction<{ whiteboardId: string; vrId: string | null }>) => {
            const { whiteboardId, vrId } = action.payload
            if (vrId) {
                state.whiteboardVrIds[whiteboardId] = vrId
            } else {
                delete state.whiteboardVrIds[whiteboardId]
            }
        },
        setWhiteboardConnectionStatus: (state, action: PayloadAction<{ whiteboardId: string; status: 'connecting' | 'connected' | 'failed' | 'timeout' }>) => {
            const { whiteboardId, status } = action.payload

            // Clear timeout if connection is successful or failed
            if (status === 'connected' || status === 'failed' || status === 'timeout') {
                const timeoutId = state.connectionTimeouts[whiteboardId]
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }
                delete state.connectionTimeouts[whiteboardId]
            }

            state.connectionStatus[whiteboardId] = status
        },
    },
})

export const { openWhiteboardDialog, closeWhiteboardDialog, setWhiteboardUrls, setWhiteboardConnectionStatus, addWhiteboardUser, removeWhiteboardUser, setWhiteboardVrId, setWhiteboardEpoch } =
    whiteboardSlice.actions

export default whiteboardSlice.reducer
